import { capitalize } from 'core/utils/word-helpers'
import { List, ListItem } from '@luminopia/react-toolbox/lib/list'
import ProgressBar from '@luminopia/react-toolbox/lib/progress_bar'
import { Expandable } from 'core/components/Expandable'
import { DosageChart } from 'core/components/prescription/dosage-chart'
import { formatPercent } from 'core/utils/number-helpers'
import * as React from 'react'
import { RouteComponentProps } from 'react-router'
import { connect, Dispatch } from 'react-redux'
import Card from 'study_reviewer/components/Card'
import { SubjectSummary } from 'study_reviewer/components/subjects'
import { AdherenceResponseItem, AdherenceAPI } from 'core/api/adherence-client'
import { IState, selectReviewers } from "../store";
import { IReviewer } from 'study_reviewer/store/reviewers/model'
import * as classNames from './SubjectPage.css'
import safeSessionStorage from 'core/store/safe-session-storage'

export interface IRouteParams {
  subjectIdentifier: string
}

interface IOwnProps extends RouteComponentProps<IRouteParams> {
  currentReviewer: IReviewer | null
}

interface IStateProps {
  role: string
  token: string
  currentReviewer: IReviewer | null
}

interface IDispatchProps { }

interface IProps extends IStateProps, IDispatchProps, IOwnProps {}

interface IComponentState {
  isUsageExpanded: boolean
  isFetching: boolean
  adherenceData: AdherenceResponseItem | null
}

interface ChartPoint {
  date: string
  completed: number
  prescribed: number
}

function getSubjectCacheKey(selectedSubject: AdherenceResponseItem): string {
  return "subjectAdherence_" + selectedSubject.accessCode;
}

export class SubjectPage extends React.Component<IProps, IComponentState> {
  private adherenceApi: AdherenceAPI;

  constructor(props: IProps) {
    super(props);

    this.adherenceApi = new AdherenceAPI(props.token);

    this.state = {
      isUsageExpanded: false,
      isFetching: true,
      adherenceData: null,
    };
  }

  async componentDidMount() {
    let selectedSubject
    const storedSubject = safeSessionStorage.get("selectedSubject");
    if (storedSubject) {
      selectedSubject = JSON.parse(storedSubject);
    }

    if (!selectedSubject) {
      this.setState({ isFetching: false });
      return;
    }

    const cacheKey = getSubjectCacheKey(selectedSubject);
    const storedAdherence = safeSessionStorage.get(cacheKey);

    if (storedAdherence) {
      const parsed = JSON.parse(storedAdherence);
      this.setState({
        adherenceData: parsed.adherenceData || null,
        isFetching: false,
      });
      return;
    }

    const { prescribingNpi, accessCode, isActive } = selectedSubject;

    try {
      const adherenceData = await this.adherenceApi.getAdherenceWithAccessCode(
        prescribingNpi,
        accessCode,
        isActive,
        true
      );
      this.setState({ adherenceData, isFetching: false });
      safeSessionStorage.set(
        cacheKey,
        JSON.stringify({
          adherenceData,
          timestamp: Date.now()
        })
      );
    } catch (error) {
      console.error("Error fetching adherence data:", error);
      this.setState({ isFetching: false });
    }
  }

  render() {
    const { role, currentReviewer } = this.props;
    const { isFetching, adherenceData } = this.state;
    const isAdmin = role === "studyAdmin";
    const percentagePrecision = isAdmin ? 2 : 0;

    if (!currentReviewer) return null;
    if (isFetching) {
      return <ProgressBar type="circular" mode="indeterminate" multicolor />;
    }
    if (!adherenceData) {
      return <h1>Patient not found</h1>;
    }

    const {
      accessCode,
      isActive,
      prescribingNpi,
      physicianName,
      amblyopicEye,
      firstDayOfTreatment,
      lastDayOfTreatment,
      pastMonthAdherence,
      totalAdherence,
      totalUsage,
      totalPrescribedHours,
      pastMonthUsage,
      pastMonthPrescribedHours,
      archivedAt
    } = adherenceData;

    return (
      <div>
        <button className={classNames.goBackButton} onClick={() => window.history.back()}>
          Go Back
        </button>
        <Card title={accessCode || "N/A"} subtitle="Patient Access Code" icon="person_outline">
          <div className={classNames.headerContainer}>
            <SubjectSummary subjectIdentifier={prescribingNpi} isTreatmentActive={isActive} isTreatmentArchived={archivedAt !== null}/>
          </div>
          <List>
            {/* Amblyopic Eye */}
            <ListItem
              legend="Amblyopic Eye"
              caption={amblyopicEye ? capitalize(amblyopicEye) : "-"}
              leftIcon="visibility"
              ripple={false}
            />
            {/* Treatment Start Date */}
            <ListItem
              legend="Treatment Start Date"
              caption={firstDayOfTreatment ? firstDayOfTreatment.split("T")[0] : "Not Started"}
              leftIcon="flag"
              ripple={false}
            />
            {/* Treatment End Date (only if inactive) */}
            <ListItem
              legend="Treatment End Date"
              caption={!isActive && lastDayOfTreatment ? lastDayOfTreatment.split("T")[0] : "-"}
              leftIcon="flag"
              ripple={false}
            />
            {/* Past Month Used (Hours) - Admin only */}
            {isAdmin && (
              <ListItem
                legend="Past Month Used (Hours)"
                caption={pastMonthUsage !== null ? pastMonthUsage.toFixed(2) : "-"}
                leftIcon="schedule"
                ripple={false}
              />
            )}
            {/* Past Month Prescribed (Hours) - Admin only */}
            {isAdmin && (
              <ListItem
                legend="Past Month Prescribed (Hours)"
                caption={pastMonthPrescribedHours !== null ? pastMonthPrescribedHours.toFixed(2) : "-"}
                leftIcon="medication"
                ripple={false}
              />
            )}
            {/* Past Month Adherence % */}
            <ListItem
              legend="Past Month Adherence % (Last 4 Weeks)"
              caption={pastMonthAdherence !== null ? formatPercent(pastMonthAdherence, percentagePrecision) : "-"}
              leftIcon="timeline"
              ripple={false}
            />
            {/* Total Used (Hours) - Admin only */}
            {isAdmin && (
              <ListItem
                legend="Total Used (Hours)"
                caption={totalUsage !== null ? totalUsage.toFixed(2) : "-"}
                leftIcon="update"
                ripple={false}
              />
            )}
            {/* Total Prescribed (Hours) - Admin only */}
            {isAdmin && (
              <ListItem
                legend="Total Prescribed (Hours)"
                caption={totalPrescribedHours !== null ? totalPrescribedHours.toFixed(2) : "-"}
                leftIcon="medication"
                ripple={false}
              />
            )}
            {/* Total Adherence % */}
            <ListItem
              legend="Total Adherence %"
              caption={totalAdherence !== null ? formatPercent(totalAdherence, percentagePrecision) : "-"}
              leftIcon="insights"
              ripple={false}
            />
            {/* Prescribing NPI */}
            <ListItem
              legend="Prescribing NPI"
              caption={prescribingNpi || "-"}
              leftIcon="medication"
              ripple={false}
            />
            {/* Physician */}
            {physicianName && (
              <ListItem
                legend="Physician"
                caption={physicianName}
                leftIcon="person"
                ripple={false}
              />
            )}
            {/* Luminopia Patient ID */}
            <ListItem
              legend="Luminopia Patient Id"
              caption={adherenceData.patientId || "-"}
              leftIcon="fingerprint"
              ripple={false}
            />
          </List>
        </Card>

        {this.recentUsageCard()}
      </div>
    );
  }

  private fillGaps(item: AdherenceResponseItem): ChartPoint[] {
    const entries = item.dailyUsageData
    if (!entries || entries.length === 0) {
      return []
    }

    const lookup = entries.reduce<Record<string, number>>((acc, { date, totalMinutes }) => {
      acc[date] = totalMinutes
      return acc
    }, {})

    // Dates are guarenteed to be returned in ascending order from backend so sorting is unecessary
    const start = new Date(entries[0].date)
    const today = new Date()
    const lastRecordedDate = new Date(entries[entries.length - 1].date)

    // we want to make sure the end date is never unbounded so it is based on available treatment
    // it will be at most 1000 days after treatment or todays date, whichever is smaller
    const maxGapEnd = new Date(lastRecordedDate)
    maxGapEnd.setDate(maxGapEnd.getDate() + 1000)
    const end = today < maxGapEnd ? today : maxGapEnd

    const out: ChartPoint[] = []
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0, 10)
      const completed = lookup[iso] !== undefined ? lookup[iso] : 0
      out.push({
        date: iso,
        completed,
        prescribed: 60,
      })
    }

    return out
  }

  private recentUsageCard() {
    const { adherenceData } = this.state;
    if (!adherenceData || !adherenceData.dailyUsageData) return null;

    return (
      <Card title="Recent Usage per Day" subtitle={`Access Code: ${adherenceData.accessCode}`} icon="show_chart">
        <List>
          <ListItem
            legend="Each blue bar in the graph shows the actual Completed Minutes per day by the patient. The red line across the graph is drawn at 60 minutes as a visual reference."
            leftIcon="information"
            ripple={false}
          />
        </List>
        <DosageChart
          width="100%"
          height={300}
          minWidth={300}
          data={this.fillGaps(adherenceData)}
        />
      </Card>
    );
  }
}

const mapStateToProps = (state: IState, ownProps: IOwnProps): IStateProps => {
  return {
    role: state.login.role,
    token: state.login.token || "",
    currentReviewer: state.reviewers.currentReviewer || null,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<void>, ownProps: IOwnProps): IDispatchProps => ({});

export default connect(mapStateToProps, mapDispatchToProps)(SubjectPage);



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_reviewer/src/pages/SubjectPage.tsx